import useLocaleText from 'components/useLocaleText'

// New literals required in the configured translation catalogue. Kept beside their UI owner
// so missing translations remain explicit; fallbacks use the actual locale, never layout direction.
export const missingWorkspaceLiterals = {
  complete: { key: 'tasks_execution_workspace_complete', fallback: { en: 'Complete task', he: 'השלמת המשימה' } },
  oneResult: { key: 'tasks_execution_one_result', fallback: { en: '1 result', he: 'תוצאה אחת' } },
  resultCount: { key: 'tasks_execution_result_count', fallback: { en: '{count} results', he: '{count} תוצאות' } },
  noAnswer: { key: 'tasks_execution_workspace_no_answer', fallback: { en: 'Not answered yet', he: 'אין תשובה עדיין' } },
  noPhoto: { key: 'tasks_execution_no_photo', fallback: { en: 'No photo yet', he: 'אין תמונה עדיין' } },
  noVideo: { key: 'tasks_execution_no_video', fallback: { en: 'No video yet', he: 'אין וידאו עדיין' } },
  noFile: { key: 'tasks_execution_workspace_no_file', fallback: { en: 'No document attached', he: 'לא צורף קובץ' } },
  opened: { key: 'tasks_execution_workspace_opened', fallback: { en: 'Opened', he: 'נפתח' } },
  notOpened: { key: 'tasks_execution_workspace_not_opened', fallback: { en: 'Not opened yet', he: 'טרם נפתח' } },
  writeAnswer: {
    key: 'tasks_execution_workspace_write_answer',
    fallback: { en: 'Type your answer…', he: 'כתבו תשובה…' },
  },
  what: { key: 'tasks_execution_workspace_what', fallback: { en: 'What to do', he: 'מה צריך לעשות' } },
  select: {
    key: 'tasks_execution_workspace_select',
    fallback: { en: 'Select a subtask to continue', he: 'בחרו תת־משימה להמשך' },
  },
  requiredLeft: {
    key: 'tasks_execution_workspace_required_left',
    fallback: { en: 'required subtasks left', he: 'תת־משימות חובה נותרו' },
  },
  requiredHint: {
    key: 'tasks_execution_workspace_required_hint',
    fallback: { en: 'Answer required before Done', he: 'נדרשת תשובה לפני סיום' },
  },
  optionalHint: {
    key: 'tasks_execution_workspace_optional_hint',
    fallback: { en: 'Optional — you can skip it', he: 'רשות — אפשר לדלג' },
  },
  next: { key: 'tasks_execution_next_subtask', fallback: { en: 'Next subtask', he: 'תת־המשימה הבאה' } },
  previous: { key: 'tasks_execution_previous_subtask', fallback: { en: 'Previous subtask', he: 'תת־המשימה הקודמת' } },
  number: { key: 'tasks_execution_workspace_number', fallback: { en: 'Number', he: 'מספר' } },
  file: { key: 'tasks_execution_file', fallback: { en: 'File', he: 'קובץ' } },
  openLink: { key: 'tasks_execution_workspace_open_link', fallback: { en: 'Open link', he: 'פתיחת קישור' } },
  externalStatus: {
    key: 'tasks_execution_workspace_external_status',
    fallback: { en: 'Completion is updated by the linked service.', he: 'ההשלמה מתעדכנת דרך השירות המקושר.' },
  },
  upload: { key: 'tasks_execution_workspace_upload', fallback: { en: 'Upload from device', he: 'העלאה מהמכשיר' } },
  retry: { key: 'tasks_execution_retry', fallback: { en: 'Retry', he: 'ניסיון חוזר' } },
  saveError: {
    key: 'tasks_execution_workspace_save_error',
    fallback: {
      en: 'Could not save. Your answer is kept. Please retry.',
      he: 'השמירה נכשלה. התשובה נשמרה בטופס. נסו שוב.',
    },
  },
  saving: { key: 'tasks_execution_workspace_saving', fallback: { en: 'Saving…', he: 'שומר…' } },
  finishAnswer: {
    key: 'tasks_execution_workspace_finish_answer',
    fallback: { en: 'Press Done to save your edited answers.', he: 'לחצו על סיום לשמירת התשובות ששונו.' },
  },
  decrease: { key: 'tasks_execution_workspace_decrease', fallback: { en: 'Decrease number', he: 'הקטנת המספר' } },
  increase: { key: 'tasks_execution_workspace_increase', fallback: { en: 'Increase number', he: 'הגדלת המספר' } },
  unsupported: {
    key: 'tasks_execution_workspace_unsupported',
    fallback: { en: 'This subtask type is not supported here.', he: 'סוג תת־המשימה אינו נתמך כאן.' },
  },
} as const

export const workspaceLiterals = {
  openError: { key: 'wrong', fallback: { en: 'Sorry, something went wrong' } },
  start: { key: 'start', fallback: { en: 'Start', he: 'התחלה' } },
  view: { key: 'subtask_view', fallback: { en: 'View' } },
  subtaskCount: { key: 'tasks_execution_subtask_count', fallback: { en: '{count} subtasks', he: '{count} תת־משימות' } },
  oneSubtask: { key: 'tasks_execution_one_subtask', fallback: { en: '1 subtask', he: 'תת־משימה אחת' } },
  back: { key: 'header_button_back', fallback: { en: 'Back to subtasks', he: 'חזרה לתת־משימות' } },
  all: { key: 'task_overview_filter_status_all', fallback: { en: 'All', he: 'הכול' } },
  subtasks: { key: 'subtasks', fallback: { en: 'Subtasks', he: 'תת־משימות' } },
  link: { key: 't_st_link', fallback: { en: 'Link', he: 'קישור' } },
  text: { key: 't_st_open_qst', fallback: { en: 'Text', he: 'טקסט' } },
  quiz: { key: 't_st_quiz', fallback: { en: 'Quiz', he: 'שאלון' } },
  poll: { key: 't_st_poll', fallback: { en: 'Poll', he: 'סקר' } },
  done: { key: 'done', fallback: { en: 'Done', he: 'סיום' } },
  required: { key: 'required', fallback: { en: 'Required', he: 'חובה' } },
  skipped: { key: 'task_status_skipped', fallback: { en: 'Skipped', he: 'דולג' } },
  // course states (ExecutionCourseProgress)
  statusTodo: { key: 'task_status_todo', fallback: { en: 'Not started', he: 'טרם התחיל' } },
  statusInProgress: { key: 'task_status_inprogress', fallback: { en: 'In progress', he: 'בתהליך' } },
  statusCompleted: { key: 'task_status_completed', fallback: { en: 'Completed', he: 'הושלם' } },
  statusFailed: { key: 'task_status_failed', fallback: { en: 'Failed', he: 'נכשל' } },
  quality: { key: 'quality', fallback: { en: 'Quality', he: 'איכות' } },
  lastRun: { key: 'last_run', fallback: { en: 'Last run', he: 'ריצה אחרונה' } },
  answer: { key: 'your_answer', fallback: { en: 'Your answer', he: 'התשובה שלך' } },
  single: { key: 't_select_one', fallback: { en: 'Select one answer', he: 'בחירת תשובה אחת' } },
  multiple: { key: 't_select_one_or_more', fallback: { en: 'Select one or more', he: 'בחירת תשובה אחת או יותר' } },
  photo: { key: 't_st_image', fallback: { en: 'Photo', he: 'תמונה' } },
  video: { key: 't_st_video', fallback: { en: 'Video', he: 'וידאו' } },
  ai: { key: 'ai_photo_subtask', fallback: { en: 'AI photo', he: 'תמונת AI' } },
  categories: { key: 'categories', fallback: { en: 'Categories', he: 'קטגוריות' } },
  category: { key: 'category', fallback: { en: 'Category', he: 'קטגוריה' } },
  votes: { key: 't_view_votes', fallback: { en: 'View votes', he: 'הצגת הצבעות' } },
  ...missingWorkspaceLiterals,
} as const

export const useWorkspaceText = () => useLocaleText(workspaceLiterals)
